import React, { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { TooltipProps } from '@mui/material/Tooltip';
import { TypographyProps } from '@mui/material/Typography';

type TruncatedTextProps = {
  text?: string | null;
  maxWidth?: number | string;
  placement?: TooltipProps['placement'];
  typographyProps?: TypographyProps;
};

/**
 * Single-line ellipsis + tooltip. Tooltip only appears when text is actually clipped.
 *
 * The Tooltip is always mounted but rendered empty when not truncated, so the
 * underlying Typography never remounts. This keeps the ref stable for the
 * ResizeObserver and avoids measurement glitches on container resize.
 */
const TruncatedText = forwardRef<HTMLElement, TruncatedTextProps>(
  ({ text, maxWidth = '100%', placement = 'top', typographyProps }, forwardedRef) => {
    const value = text ?? '';
    const innerRef = useRef<HTMLElement | null>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const setRefs = useCallback(
      (node: HTMLElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    const updateTruncated = useCallback(() => {
      const el = innerRef.current;
      if (!el || !value) {
        setIsTruncated(false);
        return;
      }
      setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    }, [value]);

    useLayoutEffect(() => {
      updateTruncated();
      const el = innerRef.current;
      if (!el) return;

      const ro = new ResizeObserver(() => updateTruncated());
      ro.observe(el);
      return () => ro.disconnect();
    }, [updateTruncated]);

    return (
      <Tooltip
        title={isTruncated ? value : ''}
        placement={placement}
        arrow
      >
        <Typography
          ref={setRefs}
          aria-label={value || undefined}
          {...typographyProps}
          noWrap
          sx={{
            maxWidth,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...typographyProps?.sx,
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    );
  },
);

TruncatedText.displayName = 'TruncatedText';

export default TruncatedText;
